
import React, { useState } from 'react';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import t from 'react-translate';
import { isEmpty } from 'underscore';

// redux
import { useAppDispatch } from 'redux/store';
import { createCollection } from 'redux/actions/collections';

// components
import ClickableContainer from 'components/clickable-container';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import NvTextArea from 'shared/components/inputs/nv-text-area';
import { config } from '../../../../config/config.json';

type CreateCollectionAndAddToCollectionProps = {
  onCancel: () => void; // closes modal
  onBackToSelect: () => void; //  go back to select mode
  onSuccess: (collectionId: number) => Promise<void> | void;
};

type FormData = {
  title: string;
  description: string;
};

const styles = css`
  .description-text {
    min-height: 120px;
  }
`;


const CreateCollectionAndAddToCollection = ({
  onCancel,
  onBackToSelect,
  onSuccess,
}: CreateCollectionAndAddToCollectionProps) => {
  const dispatch = useAppDispatch();
  const [adding, setAdding] = useState(false);

  const validationSchema = yup.object().shape({
    title: yup.string().trim()
      .required(t.VALIDATION.REQUIRED())
      .max(250, t.VALIDATION.MAX('250')),
    description: yup.string().max(255, t.VALIDATION.MAX('255')),
  });

  const methods = useForm<FormData>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      title: '',
      description: '',
    },
  });

  const { handleSubmit, formState: { isValid } } = methods;

  const onSubmit = async (data: FormData) => {
    setAdding(true);

    try {
      const res = await dispatch(
        createCollection({
          title: data.title,
          description: data.description,
          autoenroll: true,
        }),
      );

      if (isEmpty(res?.error)) {
        await onSuccess(res.payload.id); // waits for parent workflow
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <div css={styles}>
      <ClickableContainer
        onClick={onBackToSelect}
        aria-label={t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.LL_BULK_CONVERT_LECTURE_PAGES_MODAL.SELECT_EXISTING_COLLECTION_BUTTON()}
      >
        <div className='text-regular text-primary'>
          {t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.LL_BULK_CONVERT_LECTURE_PAGES_MODAL.SELECT_EXISTING_COLLECTION_BUTTON()}
        </div>
      </ClickableContainer>
      <div className='text-large-body font-weight-bolder mt-2'>{t.TIMELINE.LL_BULK_CONVERT_LECTURE_PAGES.LL_BULK_CONVERT_LECTURE_PAGES_MODAL.CREATE_NEW_COLLECTION()}</div>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <NvTextInput
            withForm
            name='title'
            ariaLabel={t.INSTITUTIONS.CONTENT_LIBRARY.NEW_COLLECTION_FORM.TITLE_ARIA_LABEL()}
            className='my-2'
            placeholder={t.INSTITUTIONS.CONTENT_LIBRARY.NEW_COLLECTION_FORM.UNTITLED_COLLECTION()}
            required
          />

          <NvTextArea
            withForm
            name='description'
            placeholder={t.INSTITUTIONS.CONTENT_LIBRARY.NEW_COLLECTION_FORM.ADD_DESCRIPTION()}
            className='mt-4 mb-2'
            textareaClassName='description-text'
          />
          {/* Actions */}
          <div className='d-flex justify-content-center mt-8 mb-6'>
            <Button
              variant='secondary'
              className='mr-2'
              onClick={onCancel}
              disabled={adding}
              data-qa={config.pendo.contentManagement.closeAddNewCollectionModal}
            >
              {t.FORM.CANCEL()}
            </Button>

            <Button
              type='submit'
              disabled={!isValid || adding}
            >
              {adding ? t.FORM.ADDING() : t.FORM.ADD()}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default CreateCollectionAndAddToCollection;
